import React, { useState , useRef} from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Button, Card, CardBody, ScrollShadow } from '@nextui-org/react';
import { postFetcher, getFetcher } from '../../../../services/api';
import toast from 'react-hot-toast';
import useAuth from '../../../../hooks/useAuth';
import ControlledReactQuill from '../../../elements/ControlledReactQuill';
import RolesAccess from '../../../../enums/roles';
import { hasValidRole } from '../../../../services/helpers';

export default function Chat({ ticketId, messages = [], mutate, className }) {
  const { user, roles } = useAuth(); // Dapatkan informasi pengguna (user/admin)
  const { register, control, handleSubmit, reset } = useForm({
    defaultValues: {
      message: '', // Default value untuk message
    },
  });
  const [isSending, setIsSending] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const chatContainerRef = useRef(null);

  const IsUserAnAdmin = hasValidRole( roles, [RolesAccess.PIC_KONSULTASI, RolesAccess.SUPER_ADMIN, RolesAccess.PIC_NOTIFIKASI, RolesAccess.PIC_PELAPORAN, RolesAccess.PIC_REGISTRASI, RolesAccess.PIC_REKOMENDASI]); // Cek apakah user adalah admin
  // Fungsi untuk mengirim pesan
  const onSubmit = async (formData) => {
    try {
      setIsSending(true);
      await postFetcher(`/api/tickets/add-message/${ticketId}`, {
        senderId: user.id, // Menggunakan ID user atau admin yang sedang login
        content: formData.message,
        isFromAdmin: IsUserAnAdmin, // Menentukan apakah pesan berasal dari admin
      });
      mutate(); // Refresh messages
      reset(); // Clear input field
      toast.success('Message sent successfully');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  // Fungsi untuk mendapatkan pesan baru
  const fetchNewMessages = async () => {
    try {
      setIsFetching(true);
      const newMessages = await getFetcher(`/api/tickets/find-messages/${ticketId}`);
      mutate((currentData) => ({
        ...currentData,
        messages: [...(currentData?.messages || []), ...newMessages],
      }));
      toast.success('New messages fetched successfully');
    } catch (error) {
      toast.error('Failed to fetch new messages');
    } finally {
      setIsFetching(false);
    }
  };

  return (
    <Card radius="sm" className={className}>
      <CardBody >
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-lg font-bold">Chat</h4>
          <Button
            size="sm"
            variant="faded"
            color="primary"
            isLoading={isFetching}
            onPress={fetchNewMessages}
          >
            Get Latest Messages
          </Button>
        </div>

        <ScrollShadow
            className="overflow-y-auto p-4"
            ref={chatContainerRef}
            style={{ height: '400px' }} // Ukuran tetap untuk tinggi komponen
            >
            {messages.length > 0 ? (
                messages.map((msg) => {
                // Logika penentuan posisi dan warna pesan
                const isFromAdmin = msg.isFromAdmin;
                const isAdminView = IsUserAnAdmin; // Mengacu pada apakah pengguna adalah admin

                const alignment = isAdminView
                    ? isFromAdmin
                    ? 'text-right'
                    : 'text-left'
                    : isFromAdmin
                    ? 'text-left'
                    : 'text-right';

                const bubbleColor = isAdminView
                    ? isFromAdmin
                    ? 'bg-blue-100 text-blue-900'
                    : 'bg-line text-ink-900'
                    : isFromAdmin
                    ? 'bg-line text-ink-900'
                    : 'bg-blue-100 text-blue-900';

                return (
                    <div key={msg.id} className={`mb-3 ${alignment}`}>
                    {/* Nama pengirim hanya ditampilkan untuk pesan dari lawan bicara */}
                    {((isAdminView && !isFromAdmin) || (!isAdminView && isFromAdmin)) && (
                        <p className="text-xs text-ink-500 mb-1">{msg.senderName || 'Admin'}</p>
                    )}
                    <div className={`inline-block rounded-lg p-3 ${bubbleColor}`}>
                        <p className="text-sm" dangerouslySetInnerHTML={{ __html: msg.content }} />
                        <span className="text-xs text-ink-500">
                        {new Date(msg.createdAt).toLocaleString()}
                        </span>
                    </div>
                    </div>
                );
                })
            ) : (
                <p className="text-sm text-ink-500 text-center">No messages yet.</p>
            )}
            </ScrollShadow>


        <form onSubmit={handleSubmit(onSubmit)} className="mt-4">
          <div className="flex items-center gap-2">
            <ControlledReactQuill
              {...register('message')}
              isRequired
              control={control}
              variant="faded"
              placeholder="Type a message..."
              className="flex-grow"
            />
            <Button type="submit" color="primary" isLoading={isSending} disabled={isSending}>
              Send
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}
